import React, { useEffect, useState } from 'react';
import { ShieldCheck, FileText } from 'lucide-react';
import QRCode from 'qrcode';

/**
 * Certificate Card — componente-assinatura do design system "Documento Certificado".
 * Reutilizado em: hero da landing, bloco Transparência, dashboard, editor (prévia),
 * página pública de validação e preview de assinatura nas configurações.
 *
 * Regras fixas:
 * - Faixa superior navy com o nome do documento.
 * - Selo "Registrado" sempre no token selo, sempre com ShieldCheck (nunca triângulo).
 * - Hash, código e URL de validação sempre em monoespaçada.
 * - Conteúdo de exemplo plausível, nunca skeleton nem lorem ipsum.
 */

export const truncateHash = (hash) => {
  if (!hash) return null;
  const clean = String(hash);
  return clean.length > 12 ? `${clean.slice(0, 4)}…${clean.slice(-4)}` : clean;
};

const QrImage = ({ value, size = 88, className = '' }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(value, {
      margin: 0,
      width: size * 2,
      color: { dark: '#101B3D', light: '#FFFFFF' },
    })
      .then((url) => { if (active) setSrc(url); })
      .catch(() => { if (active) setSrc(null); });
    return () => { active = false; };
  }, [value, size]);

  if (!src) {
    return <div style={{ width: size, height: size }} className={`bg-paper-alt rounded ${className}`} aria-hidden="true" />;
  }
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt={`QR Code de validação: ${value}`}
      className={`rounded bg-white ${className}`}
    />
  );
};

const CertificateCard = ({
  title = 'Laudo de Ultrassonografia Abdominal',
  subtitle = 'Clínica Santa Cecília, São Paulo, SP',
  patient = 'Maria A. Sousa',
  professional = 'Dr. Paulo Andrade',
  register = 'CRM 123.456-SP',
  date = '14/03/2026 · 09:42',
  hash = '8f3a1b9e2c47d05586e1f4a7b3d9c92d',
  code = 'A3B7C1',
  url = 'laudando.online/v/A3B7C1',
  status = 'registered', // 'registered' | 'draft'
  variant = 'full',      // 'full' | 'compact'
  footer = null,
  className = '',
}) => {
  const registered = status === 'registered';

  if (variant === 'compact') {
    return (
      <div className={`bg-paper border border-line rounded-card shadow-card overflow-hidden ${className}`}>
        <div className="bg-navy px-4 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-3.5 h-3.5 text-white/70 shrink-0" aria-hidden="true" />
            <span className="text-white text-xs font-semibold truncate">{title}</span>
          </div>
          {registered ? (
            <span className="inline-flex items-center gap-1 text-selo bg-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md shrink-0">
              <ShieldCheck className="w-3 h-3" aria-hidden="true" /> Registrado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-muted bg-paper-alt text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md shrink-0">
              Rascunho
            </span>
          )}
        </div>
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink truncate">{patient}</p>
            <p className="text-xs text-muted truncate">{date}</p>
          </div>
          {registered && hash && (
            <p className="font-mono text-[11px] text-muted shrink-0">SHA-256 · {truncateHash(hash)}</p>
          )}
        </div>
        {footer && <div className="px-4 pb-3">{footer}</div>}
      </div>
    );
  }

  return (
    <div className={`bg-paper border border-line rounded-card shadow-card overflow-hidden text-left ${className}`}>
      {/* Faixa superior navy */}
      <div className="bg-navy px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FileText className="w-4 h-4 text-white/70 shrink-0" aria-hidden="true" />
          <span className="text-white text-sm font-semibold truncate">{title}</span>
        </div>
        {registered && (
          <span className="inline-flex items-center gap-1.5 bg-selo text-white text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-md shrink-0">
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Registrado
          </span>
        )}
      </div>

      {/* Corpo com conteúdo representativo */}
      <div className="px-6 py-5">
        {subtitle && (
          <p className="text-[11px] font-semibold text-muted uppercase tracking-widest mb-4">{subtitle}</p>
        )}
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
          <div>
            <dt className="text-[11px] font-semibold text-muted uppercase tracking-wide">Paciente</dt>
            <dd className="text-sm font-semibold text-ink mt-0.5">{patient}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-muted uppercase tracking-wide">Responsável</dt>
            <dd className="text-sm font-semibold text-ink mt-0.5">{professional}</dd>
            <dd className="font-mono text-xs text-muted">{register}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold text-muted uppercase tracking-wide">Emitido em</dt>
            <dd className="text-sm font-semibold text-ink mt-0.5">{date}</dd>
          </div>
        </dl>
      </div>

      {/* Rodapé de verificação: QR real + hash + URL, tudo em mono */}
      <div className="border-t border-line bg-paper-alt/60 px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <QrImage value={`https://${url}`} size={72} className="border border-line p-1 shrink-0" />
        <div className="min-w-0 space-y-1">
          {hash && (
            <p className="font-mono text-xs text-ink">SHA-256 · {truncateHash(hash)}</p>
          )}
          {code && (
            <p className="font-mono text-xs text-muted">Código de registro · {code}</p>
          )}
          <p className="font-mono text-xs text-muted truncate">
            Valide em <span className="text-navy font-medium">{url}</span>
          </p>
        </div>
      </div>

      {footer && <div className="border-t border-line px-6 py-3">{footer}</div>}
    </div>
  );
};

export default CertificateCard;
